// Tournament `entry_fee` is JSONB on the backend and can arrive in any of three
// shapes, all valid:
//   - list:   [{ category: 'Open', fee: 0 }, { category: 'U-18', fee: 300 }]
//   - object: { Open: '0', 'U-18': '300' }
//   - scalar: 0 | '0' | 500 | '500'
//
// These helpers normalise across all shapes. Mirrors ccm-server's
// `hasPositiveEntryFee` (src/helper/utils.js) so client display and server
// classification agree on what "free" means: a tournament is free only when
// EVERY category fee is zero.

export type EntryFee =
  | number
  | string
  | Array<{category?: string; fee?: number | string}>
  | Record<string, number | string>
  | null
  | undefined;

/** All positive category fees present in an entry_fee of any shape. */
function positiveFees(entryFee: EntryFee): number[] {
  if (entryFee == null) {
    return [];
  }
  if (typeof entryFee === 'number' || typeof entryFee === 'string') {
    const n = Number(entryFee);
    return Number.isFinite(n) && n > 0 ? [n] : [];
  }
  if (Array.isArray(entryFee)) {
    return entryFee
      .map(e => Number(e?.fee))
      .filter(n => Number.isFinite(n) && n > 0);
  }
  if (typeof entryFee === 'object') {
    return Object.values(entryFee)
      .map(v => Number(v))
      .filter(n => Number.isFinite(n) && n > 0);
  }
  return [];
}

/** True when at least one category fee is > 0. */
export function hasPositiveEntryFee(entryFee: EntryFee): boolean {
  return positiveFees(entryFee).length > 0;
}

/**
 * Human-readable entry fee for a single-value UI slot.
 * All-zero / empty → "Free". Otherwise the lowest positive fee as "₹X"
 * (the cheapest category is the one most users can enter on).
 */
export function formatEntryFee(entryFee: EntryFee): string {
  const fees = positiveFees(entryFee);
  if (fees.length === 0) {
    return 'Free';
  }
  return `₹${Math.min(...fees)}`;
}
